// `goals`: Swift `Goal` (GroundTeam/Models/Engagement.swift): a target for a metric over a period, for one rep
// (`userId`), a team (`teamId`) or, with neither, the whole company. Decoding template `Goal(metric: .visits,
// target: 0)` (GroundTeam/Sync/ResilientDecoding.swift:133).

import { GOAL_METRICS, GOAL_PERIODS, type GoalMetric, type GoalPeriod } from '../enums';
import type { JsonObject } from '../wire';
import { newId } from '../uuid';
import {
  DATE,
  DOUBLE,
  UUID,
  createModel,
  decodeModel,
  enumOf,
  field,
  nowWire,
  type Decoded,
  type FieldMap,
  type Known,
  type Passthrough,
} from './fields';

export interface GoalPayload extends Passthrough {
  id: string;
  metric: GoalMetric;
  period: GoalPeriod;
  /** Double (money for `revenue`). */
  target: number;
  /** nil with no `teamId` = whole company. */
  userId?: string;
  teamId?: string;
  createdAt: string;
}

export const GOAL_KEYS = [
  'id',
  'metric',
  'period',
  'target',
  'userId',
  'teamId',
  'createdAt',
] as const satisfies readonly (keyof Known<GoalPayload>)[];

export const GOAL_FIELDS: FieldMap<GoalPayload> = {
  id: field.strict(UUID),
  metric: field.req(enumOf('GoalMetric', GOAL_METRICS), () => 'visits'),
  period: field.req(enumOf('GoalPeriod', GOAL_PERIODS), () => 'weekly'),
  target: field.req(DOUBLE, () => 0),
  userId: field.opt(UUID),
  teamId: field.opt(UUID),
  createdAt: field.req(DATE, nowWire),
};

export function decodeGoal(raw: JsonObject, envelopeId?: string): Decoded<GoalPayload> {
  return decodeModel(GOAL_FIELDS, raw, {}, envelopeId);
}

/** Swift's memberwise init needs `metric` and `target`. */
export type GoalInit = Partial<Known<GoalPayload>> & Pick<Known<GoalPayload>, 'metric' | 'target'>;

export function newGoal(init: GoalInit): GoalPayload {
  return createModel(GOAL_FIELDS, { id: newId(), period: 'weekly', createdAt: nowWire() }, init, 'goals');
}
